import {
  BaseUserProfile,
  EnabledFeatures,
  Gym,
  GymEnvironment,
  GymTypes,
  HandledError,
  IAccountComment,
  IBasicAuthenticationUser,
  ICancelSubscription,
  ICancelSubscriptionResponse,
  IContractDurationDetails,
  ICustomerPaymentMethod,
  IFreeWeekPhaseInfo,
  IFreeWeekSubscription,
  IFreezePhaseInfo,
  IInvoice,
  IInvoiceItem,
  IOrganisationConfig,
  IPauseSubscription,
  IPaymentService,
  IPaymentSetupData,
  IPrice,
  IStartSubscription,
  IStartSubscriptionResponse,
  IStripeCouponWrapper,
  IStripeCustomerDetailsWrapper,
  IStripeCustomerSubscriptionDataWrapper,
  IStripeSchedule,
  IStripeSchedulePhase,
  ISubscriptionAndSchedule,
  ISubscriptionCreateArguments,
  ISubscriptionPaymentSummary,
  IUpdateInvoiceParams,
  IUserDataForMemberManagement,
  IUserWorkoutAggregate,
  LeadStatus,
  MemberReportErrors,
  MembersReportData,
  ProductCategory,
  TimeSpan,
  UserAccountDetail,
  UserAccountInfo,
  UserAccountScheduleActionInfo,
} from 'fitbeat.models';
import { MemberContractDetails } from 'fitbeat.payment-service';
import moment from 'moment';
import Stripe from 'stripe';
import { IMemberDetailsData } from '../../entities/member';
import { UserProfile } from '../../entities/user/userprofile';
import { UserWorkoutSession } from '../../entities/workoutsession';
import {
  DbGymRepository,
  DbOrganisationRepository,
  IMemberReportsRepository,
  IUserProfileRepository,
  IWorkoutRepository,
} from '../../persistence';
import { AccountStatusManager } from '../accountstatus';
import { mapLeaderboardPreferencesToTeams } from '../challenge/OptInHelper';
import { DynamicLinkService } from '../dynamic-link';
import { UserProfileService } from '../user/UserProfileService';
import { IMemberManager } from './imembermanager';
import { SpecialOfferManager } from './specialOfferManager';

export const SUSSEX_ACCOUNTID = 'acct_1CHVz6CYhDMVLM8m';

export class MemberManager implements IMemberManager {
  constructor(
    private userRepo: IUserProfileRepository,
    private paymentService: IPaymentService,
    private userProfileService: UserProfileService,
    private memberReportsRepository: IMemberReportsRepository,
    private workoutRepo: IWorkoutRepository,
    private accountStatusManager: AccountStatusManager,
    private logger: any,
    private dynamicLinkService: DynamicLinkService,
    private specialOfferManager: SpecialOfferManager,
  ) { }

  public async getOrginisationConfig(gymName: string): Promise<IOrganisationConfig> {
    const gym = DbGymRepository.getByName(gymName);
    const org = gym.organisation;
    if (org == null) {
      throw new Error(`no organisation on gym ${gym?.name}`);
    }
    return DbOrganisationRepository.get(org);
  }

  public async updateAccountComment(uid: string, newComment: IAccountComment): Promise<IAccountComment[]> {
    const maxCommentLength = 1000;
    if (newComment.comment.length > maxCommentLength) {
      throw new HandledError(`Error: comment longer than ${maxCommentLength} for uid ${uid}`);
    }

    return this.userRepo.updateAccountComment(uid, newComment);
  }

  public async getAccountComments(uid: string): Promise<IAccountComment[]> {
    const userProfile = await this.userRepo.get(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }
    return userProfile.accountComments != null ? userProfile.accountComments : [];
  }

  public async updateFollowUpDate(uid: string, followUpDate: Date): Promise<Date | undefined> {
    const userProfile = await this.userRepo.getUserProfileOrThrow(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }
    return this.userRepo.updateFollowUpDate(uid, followUpDate);
  }

  public async removeFollowUpDate(uid: string): Promise<void> {
    const userProfile = await this.userRepo.getUserProfileOrThrow(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }
    delete userProfile.followUpDate;

    await this.userRepo.removeFollowUpDate(uid);
  }

  public async getFollowUpDate(uid: string): Promise<Date | undefined> {
    const userProfile = await this.userRepo.getUserProfileOrThrow(uid);
    if (!userProfile) {
      throw new Error(`user not found for ${uid}`);
    }
    return userProfile.followUpDate;
  }

  public async createMemberReport(report: MembersReportData): Promise<MembersReportData | MemberReportErrors> {
    return this.memberReportsRepository.add(report);
  }

  public async getAllMemberReports(): Promise<MembersReportData[] | undefined> {
    return this.memberReportsRepository.getAll();
  }

  public async getAllDashboardReports(): Promise<string[]> {
    const dashboardReportsIds = await this.memberReportsRepository.getAllSortedReports();
    return dashboardReportsIds;
  }

  public async getAllSortedMemberReports(): Promise<string[]> {
    const allMemberReports = await this.getAllMemberReports();
    const dashboardReportsIds = await this.memberReportsRepository.getAllSortedReports();

    if (allMemberReports) {
      const allMemberReportIds = allMemberReports.map((item: any) => item._id);
      // add missing reports
      allMemberReportIds.forEach((singeId) => {
        const reportIdExistsInAllMembers = dashboardReportsIds.find((item) => item === singeId);
        if (!reportIdExistsInAllMembers) {
          dashboardReportsIds.push(singeId);
        }
      });
      // remove deleted reports
      const filteredDashboardIds = dashboardReportsIds
        .filter((singleReportId) => allMemberReportIds.indexOf(singleReportId) !== -1);

      // update cleaned list
      await this.memberReportsRepository.updateSortedReports(filteredDashboardIds);

      return filteredDashboardIds;
    }
    return dashboardReportsIds;
  }

  public async updateSortedMemberReports(reportId: string, moveDown: boolean): Promise<string[]> {
    // get clean list
    const docs = await this.getAllSortedMemberReports();
    const reportList = docs ? [...docs] : [];
    const selectedReport = reportList.find((item: any) => item === reportId);
    if (selectedReport) {
      const reportIndex = reportList.indexOf(selectedReport);
      if (moveDown) {
        if (reportIndex + 1 < reportList.length) {
          reportList.splice(reportIndex, 1);
          reportList.splice(reportIndex + 1, 0, selectedReport);
        }
      } else if (reportIndex - 1 >= 0) {
        reportList.splice(reportIndex, 1);
        reportList.splice(reportIndex - 1, 0, selectedReport);
      }
      reportList.map((item) => item);
      await this.memberReportsRepository.updateSortedReports(reportList);
    }
    return reportList;
  }

  public async updateMemberReport(report: MembersReportData): Promise<MembersReportData> {
    return this.memberReportsRepository.update(report);
  }

  public async deleteMemberReport(reportId: string): Promise<MembersReportData> {
    return this.memberReportsRepository.remove(reportId);
  }

  public async getMemberWorkoutHistory(uid: string): Promise<UserWorkoutSession[]> {
    const includeRemovedWorkouts = true;
    const limit = 1000;
    return this.workoutRepo.getAllWorkoutsForSingleUser(uid, includeRemovedWorkouts, limit, false);
  }

  public async getMemberScheduleAndActionDetails(
    uid: string,
    gym: Gym,
    requestTime: Date,
  ): Promise<UserAccountScheduleActionInfo | undefined> {
    const userProfile = await this.userRepo.get(uid);

    if (!userProfile) {
      throw new HandledError(`User not found: ${uid}`);
    }

    const customer = await this.getCustomer(userProfile);
    const subscriptionAndSchedule = this.getSubscriptionAndSchedule(customer, requestTime);

    return {
      subscriptionSchedule: subscriptionAndSchedule && subscriptionAndSchedule.subscriptionSchedule,
      pauseSubscriptionInfo: subscriptionAndSchedule && subscriptionAndSchedule.subscription
        && await this.getPauseSubscriptionInfo(subscriptionAndSchedule.subscription, gym.accountId, requestTime),
      freeWeekSubscriptionInfo: subscriptionAndSchedule && subscriptionAndSchedule.subscription
        && await this.getFreeWeekSubscriptionInfo(subscriptionAndSchedule.subscription, gym.accountId, requestTime),
      cancelSubscriptionInfo: subscriptionAndSchedule && subscriptionAndSchedule.subscription
        && await this.getCancelSubscriptionInfo(subscriptionAndSchedule.subscription, requestTime),
      startSubscriptionInfo: await this.getStartSubscriptionInfo(subscriptionAndSchedule, customer, gym),
      subscribeEnabled: false,
      upgradeEnabled: false,
    };
  }

  public async pauseSubscription(uid: string, planId: string, start: Date, end: Date, requestDate: Date)
    : Promise<IStripeSchedule | undefined> {
    const userProfile = await this.userRepo.get(uid);
    if (!userProfile) {
      throw new HandledError(`User not found: ${uid}`);
    }

    const customer = await this.getCustomer(userProfile);

    const subscriptionAndScheduleInfo = this.getSubscriptionAndSchedule(customer, requestDate);
    const pausePhase = this.phaseBuilder(planId, start, end);

    if (subscriptionAndScheduleInfo
      && subscriptionAndScheduleInfo.hasShedule) {
      const subscriptionSchedule = await this.addPhaseToContract(
        subscriptionAndScheduleInfo,
        userProfile,
        pausePhase,
        true,
      );
      await this.userProfileService.updateUserSubscriptionDetails(
        uid,
        subscriptionAndScheduleInfo.subscription.id,
        subscriptionAndScheduleInfo.subscription.plan?.id,
      );

      return subscriptionSchedule;
    }

    if (subscriptionAndScheduleInfo
      && !subscriptionAndScheduleInfo.isContract) {
      // assumption that this is a standard plan
      return this.addPhaseToStandardPlan(subscriptionAndScheduleInfo, userProfile, pausePhase);
    }

    throw new HandledError('Could not load subscription and schedule info.');
  }

  public async cancelPause(uid: string): Promise<any> {
    throw new HandledError('Method not implemented.');
  }

  public async createSubscription(
    uid: string,
    planId: string,
    couponCode: string | undefined,
    gym: Gym,
  ): Promise<IStartSubscriptionResponse> {
    const userProfile = await this.userRepo.getUserProfileOrThrow(uid);

    const createSubscriptionArguments: ISubscriptionCreateArguments = {
      userProfile,
      planId,
      onceOffPriceIds: [],
      couponCode,
    };

    const changeResult = await this.accountStatusManager.changeAccountStatusUsingPlanId(
      userProfile,
      gym.environment,
      userProfile.planOwnerUserProfileId,
      createSubscriptionArguments,
    );
    const subscription = changeResult.data;

    await this.userProfileService.updateUserSubscriptionDetails(
      userProfile.uid,
      subscription.id,
      subscription.plan?.id,
    );

    return {
      plan: subscription.plan,
      start_date: subscription.start_date,
      id: subscription.id,
      metaData: subscription.metadata,
    };
  }

  public async cancelSubscription(
    uid: string,
    gym: Gym,
    penaltyAmount: number,
    requestTime: Date,
    isImmediateCancel: boolean = false,
  ): Promise<ICancelSubscriptionResponse> {
    const userProfile = await this.userRepo.get(uid);

    if (!userProfile) {
      throw new HandledError(`User not found: ${uid}`);
    }

    let chargeAmount = 0;
    const customer = await this.getCustomer(userProfile);
    const subscriptionAndScheduleInfo = this.getSubscriptionAndSchedule(customer, requestTime);

    if (!subscriptionAndScheduleInfo
      || !subscriptionAndScheduleInfo.subscription
      || !subscriptionAndScheduleInfo.subscription.plan) {
      throw new HandledError('User does not have a subscription');
    }

    if (subscriptionAndScheduleInfo.subscriptionSchedule) {
      const { id } = subscriptionAndScheduleInfo.subscriptionSchedule;
      this.logger.info(`Releasing schedule ${id}`);
      await this.paymentService.releaseSchedule(id, gym.accountId);
      this.logger.info(`Released schedule ${id}`);
    }

    const { subscription } = subscriptionAndScheduleInfo;
    const contractDurationDetails = MemberContractDetails.calculateContractDetails(this.logger, subscription);

    let cancelledSubscription = null;
    if (isImmediateCancel) {
      cancelledSubscription = await this.paymentService.cancelSubscriptionImmediately(
        userProfile,
        subscription.id,
        subscription.plan?.id,
      );
    } else {
      cancelledSubscription = await this.paymentService.cancelSubscriptionAtEndOfTerm(
        userProfile,
        subscription.id,
        subscription.plan?.id,
      );
    }

    if (!cancelledSubscription.plan) {
      throw new Error(`No plan found for subscription: ${subscription.id}`);
    }

    const isContract = contractDurationDetails.isContract
      && !this.hasSubscriptionOnceoffPrice(subscription);

    if (isContract && penaltyAmount > 0) {
      const charge = await this.paymentService.createPaymentIntent(
        userProfile,
        penaltyAmount * 100,
        cancelledSubscription.plan.id,
        'Cancellation penalty',
      );
      chargeAmount = charge.amount / 100;
    }

    const stripeSubscription = cancelledSubscription as Stripe.Subscription;
    await this.userProfileService.updateUserSubscriptionDetails(
      userProfile.uid,
      stripeSubscription.id,
      stripeSubscription.plan?.id,
    );

    const subscriptionEndDate = isImmediateCancel && stripeSubscription.canceled_at
      ? stripeSubscription.canceled_at
      : subscription.current_period_end;
    return {
      penaltyAmount: chargeAmount,
      cancelDate: new Date(subscriptionEndDate * 1000),
    };
  }

  public async getMembers(
    signedUpAfter?: Date,
    emailExclusionFilter?: string,
    gym?: Gym,
    workoutsDateRange?: { workoutsFromDate: Date, workoutsToDate?: Date },
  )
    : Promise<IMemberDetailsData[]> {
    if (gym === undefined) {
      throw Error('Gym is currently required to get members');
    }

    const userWorkoutAggregate: IUserWorkoutAggregate[] = await this.workoutRepo.getUserWorkoutAggregates(
      gym.name,
      gym.timezone,
      workoutsDateRange,
    );

    const users = await this.userRepo.getMemberInfoByGymName(gym.name, emailExclusionFilter);

    const filteredUsers = this.filterUsers(users, signedUpAfter);
    const profileWithAggregate = this.assignUserAggregatesToUserDetails(userWorkoutAggregate, filteredUsers);
    return this.assignUsersToMemberDetailView(profileWithAggregate);
  }

  public async getMember(uid: string, authenticatedUser: IBasicAuthenticationUser): Promise<UserProfile | undefined> {
    const userProfile = await this.userRepo.get(uid);
    const allowedToLoadForGymNames = authenticatedUser.gymNames;
    if (userProfile && userProfile.gymName && allowedToLoadForGymNames.indexOf(userProfile.gymName) === -1) {
      this.logger.warn(`${authenticatedUser.name} attempted to load member ${uid}`
        + `, but is not allowed to access gym ${userProfile.gymName}`);
      return undefined;
    }

    return userProfile;
  }

  public async getMemberDetails(uid: string, gymEnvironment: GymEnvironment): Promise<UserAccountDetail | undefined> {
    const userProfile = await this.userRepo.get(uid);
    const enabledFeatures = await this.userProfileService.getEnabledFeaturesForUser(uid, gymEnvironment);
    const hasAccessToAStudioGym = EnabledFeatures.hasAccessToAStudioGym(enabledFeatures);
    if (!userProfile) {
      return;
    }

    // set planId to default for older accounts
    if (!userProfile.currentPlanId) {
      userProfile.currentPlanId = 'DEFAULT';
    }

    const [customerDetails, customerPayments, customerInvoices] = await Promise.all([
      this.paymentService.getCustomerAndPaymentDetail(userProfile),
      this.paymentService.getCustomerPayments(userProfile),
      this.paymentService.getCustomerInvoices(userProfile),
    ]);

    if (!customerDetails) {
      throw new HandledError(`No customer found: ${userProfile.stripeUserId}`);
    }

    const defaultPaymentMethod: ICustomerPaymentMethod | undefined = await this
      .paymentService.getCustomerDefaultMethod(customerDetails.id, userProfile.currentPlanId);
    const mappedCustomer = customerDetails as IStripeCustomerDetailsWrapper;

    const currentSubscription = customerDetails.subscriptions && customerDetails.subscriptions.data[0];
    const currentSubscriptionPeriodEnd = currentSubscription?.current_period_end
      && currentSubscription.current_period_end * 1000;

    let payments;
    if (customerPayments && customerPayments.data) {
      payments = customerPayments.data.map((pmt: any) => ({
        amount: pmt.amount,
        currency: pmt.currency,
        paid: pmt.paid,
        status: pmt.status,
        description: pmt.description,
        created: pmt.created,
        refunded: pmt.refunded,
        invoiceNo: pmt.invoice,
      }));
    }

    const activeSpecialOffer = await this.specialOfferManager.getActiveSpecialOfferForDisplay(userProfile);

    let validPlansForSubscription;
    let currentAccountStatus;
    let standardMembershipPlansLink;
    if (userProfile.gymName) {
      const gym = DbGymRepository.getByName(userProfile.gymName);
      currentAccountStatus = gym && await this.accountStatusManager.getUserCurrentAccountStatus(
        userProfile,
        gym.environment,
      );
      standardMembershipPlansLink = await this.dynamicLinkService.createMembershipPlansLink(
        userProfile.gymName,
        userProfile.email,
      );
      validPlansForSubscription = this.getPlansForSpecialOffers(mappedCustomer.currency, gym);
    }

    return {
      userInfo: {
        hasAccessToAStudioGym,
        delinquent: mappedCustomer.delinquent,
        shippingAddress: mappedCustomer.shipping && mappedCustomer.shipping.address,
        currency: mappedCustomer.currency,
        defaultPaymentMethod,
        profile: userProfile,
        specialOfferEnabled:
          !!currentAccountStatus && this.specialOfferManager.isSpecialOfferEnabled(currentAccountStatus),
        ...this.mapToUserAccount(userProfile)!,
        ...(activeSpecialOffer && { activeSpecialOffer }),
        ...(currentAccountStatus && { currentAccountStatus }),
        ...(currentSubscriptionPeriodEnd && { currentSubscriptionPeriodEnd }),
        ...(validPlansForSubscription && { validPlansForSubscription }),
        ...(standardMembershipPlansLink && { standardMembershipPlansLink }),
      },
      payments,
      invoices: customerInvoices,
    };
  }

  public async updateMember(uid: string, updatedMember: UserProfile): Promise<any> {
    return this.userProfileService.updateMemberDetails(uid, updatedMember);
  }

  public async getMerchandise(gym: Gym, uid: string): Promise<IPrice[]> {
    const userProfile = await this.userRepo.getUserProfileOrThrow(uid);
    const customer = await this.getCustomer(userProfile);
    const allPrices = this.paymentService.getCachedPrices();

    return this.filterPrices(allPrices, gym.accountId, customer.currency, [ProductCategory.Goods]);
  }

  public async freeWeekSubscription(uid: string, planId: string, start: Date, end: Date, requestDate: Date)
    : Promise<IStripeSchedule | undefined> {
    const userProfile = await this.userRepo.get(uid);
    if (!userProfile) {
      throw new HandledError(`User not found: ${uid}`);
    }

    const customer = await this.getCustomer(userProfile);

    const subscriptionAndScheduleInfo = this.getSubscriptionAndSchedule(customer, requestDate);

    const freeWeekPhase = this.phaseBuilder(planId, start, end);

    if (subscriptionAndScheduleInfo
      && subscriptionAndScheduleInfo.hasShedule) {
      return this.addPhaseToContract(subscriptionAndScheduleInfo, userProfile, freeWeekPhase);
    }

    if (subscriptionAndScheduleInfo
      && !subscriptionAndScheduleInfo.isContract) {
      // assumption that this is a standard plan
      return this.addPhaseToStandardPlan(subscriptionAndScheduleInfo, userProfile, freeWeekPhase);
    }

    throw new HandledError('Could not load subscription and schedule info.');
  }

  public async getCoupons(
    uid: string,
    gym: Gym,
    priceId?: string,
    productCategories?: ProductCategory[],
  ): Promise<IStripeCouponWrapper[]> {
    const userProfile = await this.userRepo.getUserProfileOrThrow(uid);
    const customer: IStripeCustomerDetailsWrapper = await this.getCustomer(userProfile);

    let coupons: IStripeCouponWrapper[] = [];

    if (priceId) {
      const prices = this.paymentService.getCachedPrices();
      const price = prices.find((x: IPrice) => x.priceId === priceId);

      if (!price?.product) {
        throw Error(`No product linked to price: ${priceId}`);
      }

      coupons = await this.paymentService.getCouponsForProduct(price.product.id);

      return this.filterCoupons(coupons, gym.accountId, price.currency, productCategories);
    }

    coupons = await this.paymentService.getStripeCoupons();
    return this.filterCoupons(coupons, gym.accountId, customer.currency || undefined, productCategories);
  }

  public async createSetupIntent(
    uid: string,
    gym: Gym,
  ): Promise<IPaymentSetupData> {
    const userProfile = await this.userRepo.getUserProfileOrThrow(uid);
    const customer: IStripeCustomerDetailsWrapper = await this.getCustomer(userProfile);
    const paymentMethods = this.paymentService.getPaymentMethodsFromAccountId(gym.accountId);

    return this.paymentService.createSetupIntentForCustomer(
      customer.id,
      gym.accountId,
      paymentMethods,
    );
  }

  public async updateLeadStatus(uid: string, leadStatus: LeadStatus): Promise<UserProfile> {
    if (leadStatus.length > 100) {
      throw new Error(`leadStatus to long for uid ${uid}`);
    }
    const dbUser = await this.userRepo.getUserProfileOrThrow(uid);
    dbUser.leadStatus = leadStatus;
    await this.userRepo.update(dbUser);
    return dbUser;
  }

  public async finaliseSetupIntent(
    uid: string,
    gym: Gym,
    setupIntentId: string,
    saveAsDefaultPaymentMethod?: boolean,
  ):
    Promise<IPaymentSetupData> {
    const userProfile: BaseUserProfile = await this.userRepo.getUserProfileOrThrow(uid);

    return this.paymentService.attachPaymentMethodToCustomer(
      gym.accountId,
      userProfile.stripeUserId,
      setupIntentId,
      saveAsDefaultPaymentMethod,
    );
  }

  public async getPaymentSummary(uid: string, priceId: string, couponCode: string | undefined):
  Promise<ISubscriptionPaymentSummary | undefined> {
    const userProfile = await this.userRepo.getUserProfileOrThrow(uid);
    const paymentSummary = await this.paymentService.getSubscriptionPaymentSummary(userProfile, priceId, couponCode);

    return paymentSummary !== null ? paymentSummary : undefined;
  }

  public async getInvoice(gym: Gym, invoiceId: string): Promise<IInvoice> {
    return this.paymentService.getInvoiceByAccountId(gym.accountId, invoiceId);
  }

  public async deleteInvoice(gym: Gym, invoiceId: string): Promise<void> {
    const invoice = await this.paymentService.getInvoiceByAccountId(gym.accountId, invoiceId);

    if (invoice.status === 'paid') {
      throw new Error(`Cannot change paid invoice ${invoiceId}`);
    }

    return invoice.status === 'draft'
      ? this.paymentService.deleteInvoice(gym.accountId, invoiceId)
      : this.paymentService.voidInvoice(gym.accountId, invoiceId);
  }

  public async createInvoice(gym: Gym, uid: string): Promise<IInvoice> {
    const userProfile = await this.userRepo.getUserProfileOrThrow(uid);
    const customer = await this.getCustomer(userProfile);

    return this.paymentService.createInvoice(gym.accountId, customer.id);
  }

  public async finaliseAndPayInvoice(gym: Gym, invoiceId: string, paymentMethodId: string, note?: string):
  Promise<IInvoice> {
    let invoice = await this.paymentService.getInvoiceByAccountId(gym.accountId, invoiceId);

    if (note && invoice.status === 'draft') {
      this.logger.info(`Updating invoice with note ${note}`);
      await this.paymentService.updateInvoice(gym.accountId, invoiceId, {
        footer: note,
      });
    }

    if (invoice.status === 'draft') {
      this.logger.info(`Finalising invoice ${invoiceId}`);
      invoice = await this.paymentService.finaliseInvoice(gym.accountId, invoiceId);
    }

    if (invoice.status !== 'paid') {
      this.logger.info(`Paying invoice ${invoiceId}`);
      invoice = await this.paymentService.payInvoice(gym.accountId, invoiceId, {
        ...(paymentMethodId ? { payment_method: paymentMethodId } : {}),
      });
    }

    return invoice;
  }

  public async finaliseInvoice(gym: Gym, invoiceId: string, note?: string):
  Promise<{
    invoice: IInvoice,
    paymentIntent: Stripe.PaymentIntent,
  }> {
    let invoice = await this.paymentService.getInvoiceByAccountId(gym.accountId, invoiceId);

    if (note && invoice.status === 'draft') {
      this.logger.info(`Updating invoice with note ${note}`);
      await this.paymentService.updateInvoice(gym.accountId, invoiceId, {
        footer: note,
      } as any);
    }

    if (invoice.status === 'draft') {
      this.logger.info(`Finalising invoice ${invoiceId}`);
      invoice = await this.paymentService.finaliseInvoice(gym.accountId, invoiceId);
    }
    const paymentIntent = await this.paymentService.createPaymentIntentFromInvoiceForTerminal(
      invoice,
      gym.accountId,
    );

    return {
      invoice,
      paymentIntent,
    };
  }

  public async markInvoiceAsPaidOutOfBand(gym: Gym, invoiceId: string):
  Promise<IInvoice> {
    const invoice = await this.paymentService.payInvoice(gym.accountId, invoiceId, {
      paid_out_of_band: true,
    });
    return invoice;
  }

  public async updateInvoiceCoupon(gym: Gym, invoiceId: string, couponCode?: string): Promise<IInvoice> {
    const updateParams: IUpdateInvoiceParams = {
      discounts: couponCode ? [{
        coupon: couponCode,
      }] : [],
    };

    return this.paymentService.updateInvoice(gym.accountId, invoiceId, updateParams);
  }

  public async updateMerchandiseInvoiceItem(gym: Gym, uid: string, item: IInvoiceItem, invoiceId?: string):
  Promise<IInvoice> {
    const userProfile = await this.userRepo.getUserProfileOrThrow(uid);
    const customer = await this.getCustomer(userProfile);

    if (!item.price) {
      throw new Error('No price provided for invoice item');
    }

    const definedInvoiceId: string = invoiceId ?? (await this.createInvoice(gym, uid)).invoiceNo;

    if (!item.id) {
      return this.paymentService.createInvoiceItem(gym.accountId, {
        price: item.price.priceId,
        invoice: definedInvoiceId,
        quantity: item.quantity,
        customer: customer.id,
      });
    }

    if (item.quantity === 0) {
      return this.paymentService.deleteInvoiceItem(gym.accountId, definedInvoiceId, item.id);
    }

    return this.paymentService.updateInvoiceItem(gym.accountId, item.id, {
      price: item.price.priceId,
      quantity: item.quantity,
    });
  }

  public async setCustomerDefaultPaymentMethod(
    gym: Gym,
    uid: string,
    paymentMethodId: string,
  ): Promise<IStripeCustomerDetailsWrapper> {
    const userProfile = await this.userRepo.getUserProfileOrThrow(uid);

    return await this.paymentService.setCustomerDefaultPaymentMethod(
      gym.accountId,
      userProfile.stripeUserId,
      paymentMethodId,
    ) as IStripeCustomerDetailsWrapper;
  }

  private mapToUserAccount(userProfile: IUserDataForMemberManagement): UserAccountInfo | undefined {
    const userAccount: any = {
      ...userProfile.stripeSubscriptionData,
      contractEnd: userProfile.stripeSubscriptionData?.contractEndDate,
      contractStart: userProfile.stripeSubscriptionData?.contractStartDate,
      planExpiresAt: UserProfile.planExpiresAt(userProfile.stripeSubscriptionData),
      cancelAt: userProfile.stripeSubscriptionData?.subscriptionCancelsAt,
    };
    return userAccount;
  }

  private assignUsersToMemberDetailView(
    users: (IUserDataForMemberManagement & IUserWorkoutAggregate)[],
  )
    : IMemberDetailsData[] {
    return [
      ...users.map((user) => ({
        userSettings: user,
        userAccount: this.mapToUserAccount(
          user,
        ),
        userWorkoutAggregate: {
          totalCompletedStudio: user.totalCompletedStudio,
          totalMissedStudio: user.totalMissedStudio,
          totalCompleted: user.totalCompleted,
          totalMissedHome: user.totalMissedHome,
          totalCompletedHome: user.totalCompletedHome,
          totalHRCompleted: user.totalHRCompleted,
          totalBurnCompleted: user.totalBurnCompleted,
          totalBuildCompleted: user.totalBuildCompleted,
          totalBalanceCompleted: user.totalBalanceCompleted,
          totalCancelledStudio: user.totalCancelledStudio,
          totalCustomisedCompleted: user.totalCustomisedCompleted,
          totalDurationInMinutes: user.totalDurationInMinutes,
          totalEffortPoints: user.totalEffortPoints,
          totalCalories: user.totalCalories,
          firstWorkout: user.firstWorkout,
          lastWorkout: user.lastWorkout,
          totalStudio28mins: user.totalStudio28mins,
          totalStudio48mins: user.totalStudio48mins,
          totalHome17mins: user.totalHome17mins,
          totalHome29mins: user.totalHome29mins,
          totalHome42mins: user.totalHome42mins,
        },
      })),
    ];
  }

  private assignUserAggregatesToUserDetails(
    userWorkoutAggregate: IUserWorkoutAggregate[],
    filteredUsers: IUserDataForMemberManagement[],
  ): (IUserDataForMemberManagement & IUserWorkoutAggregate)[] {
    const userProfilesWithAggregates: (IUserDataForMemberManagement & IUserWorkoutAggregate)[] = [];

    for (const singleUserProfile of filteredUsers) {
      const userGymAggregates = userWorkoutAggregate.find(
        (singleAggregateResult) => singleUserProfile.uid === singleAggregateResult.userProfileId,
      );
      const allTeams = mapLeaderboardPreferencesToTeams(singleUserProfile);
      singleUserProfile.teams = allTeams;
      const userAggregate: IUserWorkoutAggregate = {
        totalCompleted: userGymAggregates?.totalCompleted || 0,
        totalMissedStudio: userGymAggregates?.totalMissedStudio || 0,
        totalCompletedStudio: userGymAggregates?.totalCompletedStudio || 0,
        totalMissedHome: userGymAggregates?.totalMissedHome || 0,
        totalCompletedHome: userGymAggregates?.totalCompletedHome || 0,
        totalHRCompleted: userGymAggregates?.totalHRCompleted || 0,
        totalBurnCompleted: userGymAggregates?.totalBurnCompleted || 0,
        totalBuildCompleted: userGymAggregates?.totalBuildCompleted || 0,
        totalBalanceCompleted: userGymAggregates?.totalBalanceCompleted || 0,
        totalCancelledStudio: userGymAggregates?.totalCancelledStudio || 0,
        totalCustomisedCompleted: userGymAggregates?.totalCustomisedCompleted || 0,
        totalDurationInMinutes: userGymAggregates?.totalDurationInMinutes || 0,
        totalCalories: userGymAggregates?.totalCalories || 0,
        totalEffortPoints: userGymAggregates?.totalEffortPoints || 0,
        firstWorkout: userGymAggregates?.firstWorkout || 0,
        lastWorkout: userGymAggregates?.lastWorkout || 0,
        totalStudio28mins: userGymAggregates?.totalStudio28mins || 0,
        totalStudio48mins: userGymAggregates?.totalStudio48mins || 0,
        totalHome17mins: userGymAggregates?.totalHome17mins || 0,
        totalHome29mins: userGymAggregates?.totalHome29mins || 0,
        totalHome42mins: userGymAggregates?.totalHome42mins || 0,
      };
      const updatedProfile = Object.assign(userAggregate, singleUserProfile);
      userProfilesWithAggregates.push(updatedProfile);
    }
    return userProfilesWithAggregates;
  }

  private filterUsers(users: IUserDataForMemberManagement[], signedUpAfter?: Date):
  IUserDataForMemberManagement[] {
    return users.filter((user: IUserDataForMemberManagement) => !signedUpAfter
      || new Date(user.userSignupDate).getTime() >= signedUpAfter.getTime());
  }

  private getSubscriptionAndSchedule(
    customerInfo: IStripeCustomerDetailsWrapper,
    requestTime: Date,
  ): ISubscriptionAndSchedule | undefined {
    if (customerInfo && customerInfo.subscriptions && customerInfo.subscriptions.data.length > 0) {
      const subscription: IStripeCustomerSubscriptionDataWrapper = customerInfo.subscriptions.data[0];

      const { schedule } = subscription;
      const { isContract, contractStart } = MemberContractDetails.calculateContractDetails(this.logger, subscription);

      let subscriptionSchedule: IStripeSchedule | undefined;
      if (isContract || schedule) {
        let phases;
        if (schedule && schedule.phases) {
          phases = schedule.phases;
        }

        if (schedule) {
          const endBehavior = 'release';
          subscriptionSchedule = {
            id: schedule.id,
            end_behavior: 'release',
            current_phase: schedule && schedule.current_phase,
            customer: customerInfo.id,
            phases: phases ? phases.map((phase: IStripeSchedulePhase, index, arr) => {
              const phaseBefore: IStripeSchedulePhase | undefined = (arr[index - 1]);
              const phaseAfter: IStripeSchedulePhase | undefined = (arr[index + 1]);
              const isLastPhase = arr.length - 1 === index;
              return {
                start_date: phase.start_date * 1000,
                end_date: phase.end_date * 1000,
                freezePhaseInfo: this.getFreezePhaseInfo(
                  phase,
                  phaseBefore,
                  phaseAfter,
                  requestTime,
                  contractStart ? new Date(contractStart) : undefined,
                ),
                freeWeekPhaseInfo: this.getFreeWeekPhaseInfo(
                  phase,
                  phaseBefore,
                  phaseAfter,
                  requestTime,
                  contractStart ? new Date(contractStart) : undefined,
                ),
                isInfinite: isLastPhase && endBehavior === 'release',
                plans: phase.plans && phase.plans.map(({ plan }) => {
                  const priceInfo = this.paymentService.getPriceInfo(plan);

                  if (!priceInfo) {
                    throw new Error(`No such price found: ${plan}`);
                  }

                  return {
                    plan: priceInfo.priceId,
                    description: priceInfo && priceInfo.description,
                  };
                }),
              } as IStripeSchedulePhase;
            }) : [],
          };
        }
      }

      return {
        isContract,
        hasShedule: !!(schedule),
        subscription,
        subscriptionSchedule,
      };
    }
    return undefined;
  }

  private async getFreeWeekSubscriptionInfo(
    subscription: IStripeCustomerSubscriptionDataWrapper,
    accountId: string,
    requestTime: Date,
  ): Promise<IFreeWeekSubscription> {
    if (!subscription.plan) {
      throw new HandledError(`Failed to get weekly subscription info for subscription ${subscription.id}`);
    }

    if (subscription.cancel_at) {
      return {
        allowedPlanIds: await this.getAllowedFreeWeekPlanIds(accountId),
        message: 'Cannot add freeze to cancelled subscription',
        enabled: false,
      };
    }

    let message;
    let contractEndDate;
    const contractDurationDetails = MemberContractDetails.calculateContractDetails(this.logger, subscription);
    const hasSchedule = contractDurationDetails.hasShedule;
    const { isContract } = contractDurationDetails;

    let enabled = (subscription.status === 'active')
      && !this.isFreezeOrFreePrice(subscription.plan);

    if (hasSchedule) {
      return {
        allowedPlanIds: await this.getAllowedFreeWeekPlanIds(accountId),
        message: 'This subscription has a schedule therefore free weeks need to be added on the schedule',
        enabled: false,
      };
    }

    if (hasSchedule && isContract && !contractDurationDetails.contractEnd) {
      message = 'No contract end date found.';
      enabled = false;
    } else {
      contractEndDate = contractDurationDetails.contractEnd;
    }
    if (!enabled && !isContract) {
      message = 'The free weeks are disabled due to plan not being a limited duration contract.';
    }
    if (!enabled && subscription.status !== 'active' && isContract) {
      message = 'The free weeks are disabled due to the subscription not being on a active plan with a schedule.';
    }

    if (this.isFreezeOrFreePrice(subscription.plan)) {
      message = 'The free weeks are disabled due to the subscription already being on a free or freeze price.';
    }

    if (!enabled) {
      return {
        allowedPlanIds: await this.getAllowedFreeWeekPlanIds(accountId),
        enabled,
        message,
      };
    }

    const earliestStart = moment(requestTime).add(1, 'day').toDate();
    const earliestEnd = moment(earliestStart).add(7, 'day').toDate();
    let latestStart = moment(requestTime).add(1, 'year').subtract(8, 'day').toDate();
    let latestEnd = moment(requestTime).add(1, 'year').subtract(7, 'day').toDate();
    if (contractEndDate) {
      latestStart = moment(contractEndDate).subtract(8, 'day').toDate();
      latestEnd = moment(contractEndDate).subtract(7, 'day').toDate();
    }

    return {
      enabled,
      allowedPlanIds: await this.getAllowedFreeWeekPlanIds(accountId),
      earliestStart,
      earliestEnd,
      latestStart,
      latestEnd,
    };
  }

  private async getPauseSubscriptionInfo(
    subscription: IStripeCustomerSubscriptionDataWrapper,
    accountId: string,
    requestTime: Date,
  ): Promise<IPauseSubscription> {
    let message;
    let contractEndDate;
    let freePauseDurationAllowed = false;
    let extendedContractDays = 0;
    const cancelEnabled = false;
    const contractDurationDetails = MemberContractDetails.calculateContractDetails(this.logger, subscription);
    const hasSchedule = contractDurationDetails.hasShedule;
    const { isContract } = contractDurationDetails;

    if (!subscription.plan) {
      throw new HandledError(`Failed to get pause subscription info for subscription: ${subscription.id}`);
    }

    if (subscription.cancel_at) {
      return {
        allowedPlanIds: await this.getAllowedPlanIds(accountId),
        message: 'Cannot add freeze to cancelled subscription',
        enabled: false,
      };
    }

    let enabled = (subscription.status === 'active')
      && !this.isFreezeOrFreePrice(subscription.plan);

    if (hasSchedule) {
      return {
        allowedPlanIds: await this.getAllowedPlanIds(accountId),
        message: 'This subscription has a schedule therefore freezes need to be added on the schedule',
        enabled: false,
      };
    }

    if (hasSchedule && isContract && !contractDurationDetails.contractEnd) {
      message = 'No contract end date found.';
      enabled = false;
    } else {
      contractEndDate = contractDurationDetails.contractEnd;
      extendedContractDays = contractDurationDetails.extendedContractInDays
        ? contractDurationDetails.extendedContractInDays : 0;
    }
    if (!enabled && !isContract) {
      message = 'The pause is disabled due to plan not being a limited duration contract.';
    }
    if (!enabled && subscription.status !== 'active' && isContract) {
      message = 'The pause is disabled due to the subscription not being on a active plan with a schedule.';
    }

    if (this.isFreezeOrFreePrice(subscription.plan)) {
      message = 'The freeze is disabled due to the subscription already being on a free or freeze price.';
    }

    if (!enabled) {
      return {
        allowedPlanIds: await this.getAllowedPlanIds(accountId),
        enabled,
        message,
        freePauseDurationAllowed,
        cancelEnabled,
      };
    }
    if (subscription.plan.metadata.freePauseDaysAllowed) {
      const freePauseDaysAllowed = parseInt(subscription.plan.metadata.freePauseDaysAllowed, 10);
      const remainingFreePauseDays = freePauseDaysAllowed - extendedContractDays;
      freePauseDurationAllowed = remainingFreePauseDays > freePauseDaysAllowed;

      if (!freePauseDurationAllowed) {
        message = `Warning: already used free pause quota (${freePauseDaysAllowed} days), use paid pause.`;
      }
    }

    const earliestStart = moment(requestTime).add(1, 'day').toDate();

    const earliestEnd = moment(earliestStart).add(7, 'day').toDate();
    let latestStart = moment(requestTime).add(1, 'year').subtract(8, 'day').toDate();
    let latestEnd = moment(requestTime).add(1, 'year').subtract(7, 'day').toDate();
    if (contractEndDate) {
      latestStart = moment(contractEndDate).subtract(8, 'day').toDate();
      latestEnd = moment(contractEndDate).subtract(7, 'day').toDate();
    }

    return {
      enabled,
      freePauseDurationAllowed,
      message,
      allowedPlanIds: await this.getAllowedPlanIds(accountId),
      earliestEnd,
      earliestStart,
      latestStart,
      latestEnd,
      cancelEnabled,
    };
  }

  private getFreeWeekPhaseInfo(
    phase: IStripeSchedulePhase,
    phaseBefore: IStripeSchedulePhase | undefined,
    phaseAfter: IStripeSchedulePhase | undefined,
    requestTime: Date,
    contractStartDate?: Date,
  ): IFreeWeekPhaseInfo {
    const isContract = !!contractStartDate;

    if (!phase.plans[0]?.plan) {
      throw new Error('No plan found for phase');
    }

    const price = this.paymentService.getPriceInfo(phase.plans[0].plan);

    if (!price) {
      throw new Error(`No price found for: ${price}`);
    }

    let message;
    const priceIsAFreezeOrFreeWeek = this.isFreezeOrFreePrice(price);
    const phaseHasAlreadyCompleted = new Date(phase.end_date * 1000) < requestTime;
    const enabled = !priceIsAFreezeOrFreeWeek && !phaseHasAlreadyCompleted;

    if (priceIsAFreezeOrFreeWeek) {
      message = 'The freeze is disabled due to the phase already being on a free or freeze price.';
    }

    if (phaseHasAlreadyCompleted) {
      message = 'Phase has already completed.';
    }
    if (!enabled) {
      return {
        enabled,
        message,
      };
    }

    const isLastPhase = !phaseAfter;
    const {
      earliestStart, earliestEnd, latestStart, latestEnd,
    } = this.calculatePhaseStartAndEndDates(
      phase,
      phaseBefore,
      phaseAfter,
      requestTime,
      isContract,
      isLastPhase,
      contractStartDate,
    );

    if (moment(latestEnd).diff(moment(earliestStart), 'day') < 1) {
      return {
        enabled: false,
        message: 'phase must be at least 1 day long',
      };
    }

    if (!isContract && isLastPhase) {
      return {
        enabled: true,
        earliestStart,
        latestStart,
        earliestEnd,
        latestEnd,
      } as IFreezePhaseInfo;
    }

    const isPhaseBeforeContract = isContract && new Date(phase.start_date * 1000) < contractStartDate;
    if (isPhaseBeforeContract) {
      return {
        enabled: false,
        message: 'Cannot freeze phases before the start of the contract',
      };
    }

    if (isContract && isLastPhase) {
      return {
        enabled: false,
        message: 'Cannot add freeze to last phase of a contract',
      } as IFreezePhaseInfo;
    }

    return {
      enabled,
      message,
      earliestStart,
      latestStart,
      earliestEnd,
      latestEnd,
    } as IFreezePhaseInfo;
  }

  private getFreezePhaseInfo(
    phase: IStripeSchedulePhase,
    phaseBefore: IStripeSchedulePhase | undefined,
    phaseAfter: IStripeSchedulePhase | undefined,
    requestTime: Date,
    contractStartDate: Date | undefined,
  ): IFreezePhaseInfo {
    let message;
    const freePauseDurationAllowed = false;
    const cancelEnabled = false;
    const isContract = !!contractStartDate;

    const planId = phase.plans[0]?.plan;
    if (!planId) {
      throw new Error('No plan found for phase');
    }
    const price = this.paymentService.getPriceInfo(planId);

    if (!price) {
      throw new Error(`No price found for: ${price}`);
    }

    const priceIsAFreezeOrFreeWeek = this.isFreezeOrFreePrice(price);
    const phaseHasAlreadyCompleted = new Date(phase.end_date * 1000) < requestTime;
    const enabled = !priceIsAFreezeOrFreeWeek && !phaseHasAlreadyCompleted;

    if (priceIsAFreezeOrFreeWeek) {
      message = 'The freeze is disabled due to the phase already being on a free or freeze price.';
    }

    if (phaseHasAlreadyCompleted) {
      message = 'Phase has already completed.';
    }

    if (!enabled) {
      return {
        enabled,
        message,
        freePauseDurationAllowed,
        cancelEnabled,
      } as IFreezePhaseInfo;
    }

    if (price.metadata?.freePauseDaysAllowed) {
      const remainingFreeFreezeDays = this.calculateRemainingFreeFreezeDays(price, phase);
      if (remainingFreeFreezeDays) {
        message = `Warning: already used free pause quota (${remainingFreeFreezeDays} days), use paid pause.`;
      }
    }

    const isLastPhase = !phaseAfter;
    const {
      earliestStart, earliestEnd, latestStart, latestEnd,
    } = this.calculatePhaseStartAndEndDates(
      phase,
      phaseBefore,
      phaseAfter,
      requestTime,
      isContract,
      isLastPhase,
      contractStartDate,
    );

    if (moment(latestEnd).diff(moment(earliestStart), 'day') < 1) {
      return {
        enabled: false,
        message: 'phase must be at least 1 day long',
      };
    }

    if (!isContract && isLastPhase) {
      return {
        enabled: true,
        earliestStart,
        latestStart,
        earliestEnd,
        latestEnd,
      } as IFreezePhaseInfo;
    }

    const isPhaseBeforeContract = isContract && new Date(phase.start_date * 1000) < contractStartDate;
    if (isPhaseBeforeContract) {
      return {
        enabled: false,
        message: 'Cannot freeze phases before the start of the contract',
      };
    }

    if (isContract && isLastPhase) {
      return {
        enabled: false,
        message: 'Cannot add freeze to last phase of a contract',
      } as IFreezePhaseInfo;
    }

    return {
      enabled,
      freePauseDurationAllowed,
      message,
      earliestStart,
      latestStart,
      earliestEnd,
      latestEnd,
      cancelEnabled,
    } as IFreezePhaseInfo;
  }

  private calculatePhaseStartAndEndDates(
    phase: IStripeSchedulePhase,
    phaseBefore: IStripeSchedulePhase | undefined,
    phaseAfter: IStripeSchedulePhase | undefined,
    requestTime: Date,
    isContract: boolean,
    isLastPhase: boolean,
    contractStartDate: Date | undefined,
  ) {
    const earliestStart = this.getEarliestStart(phase, phaseBefore, requestTime, contractStartDate);

    let latestEnd = this.getLatestEnd(phase, phaseAfter);
    let latestStart = moment(latestEnd).subtract(1, 'day').toDate();

    if (!isContract && isLastPhase) {
      latestStart = moment(requestTime).add(1, 'year').subtract(1, 'day').toDate();
      latestEnd = moment(requestTime).add(1, 'year').toDate();
    }

    const earliestEnd = new Date(
      Math.min(
        latestEnd.getTime(),
        moment(earliestStart).add(7, 'days').toDate().getTime(),
      ),
    );

    return {
      earliestStart,
      earliestEnd,
      latestStart,
      latestEnd,
    };
  }

  private calculateRemainingFreeFreezeDays(price: IPrice, phase: IStripeSchedulePhase) {
    const {
      originalContractDuration, originalContractDurationUnit,
    } = price.metadata;
    const originalDuration = TimeSpan.getAddedDurationInUnitsFromDate(
      new Date(phase.start_date * 1000),
      parseInt(originalContractDuration as string, 10),
      originalContractDurationUnit,
    );
    const extendedContractInDays = moment(phase.end_date * 1000).diff(new Date(originalDuration), 'days');
    const freePauseDaysAllowed = parseInt(price.metadata.freePauseDaysAllowed, 10);
    const remainingFreePauseDays = freePauseDaysAllowed - extendedContractInDays;
    return remainingFreePauseDays > freePauseDaysAllowed ? remainingFreePauseDays : undefined;
  }

  private isFreezeOrFreePrice(price: { metadata: any }) {
    return !!(price.metadata?.isPausePlan || price.metadata?.isFreePlan);
  }

  private async getAllowedPlanIds(gymAccountId: string) {
    const pausePlans: Array<{
      price: { nickname: string | null, id: string },
      accountId: string,
    }> = await this.paymentService.getPausePlans(gymAccountId);

    return pausePlans && pausePlans.map(({ price, accountId }) => ({
      name: price.nickname,
      price: price.id,
      accountId,
    })) || [];
  }

  private async getAllowedFreeWeekPlanIds(gymAccountId: string) {
    const freePlans: Array<{
      price: { nickname: string | null, id: string },
      accountId: string,
    }> = await this.paymentService.getFreeWeekPlans(gymAccountId);
    return freePlans && freePlans.map(({ price, accountId }) => ({
      name: price.nickname,
      price: price.id,
      accountId,
    }));
  }

  private getAllowedPlansForStartSubscription(customerCurrency: string | null, gym: Gym) {
    const plans = this.paymentService.getCachedPrices();
    const filteredPlans: IPrice[] = this.filterPrices(
      plans,
      gym.accountId,
      customerCurrency,
      [ProductCategory.Services],
    );

    if (gym.type === GymTypes.Home && gym.allowedOnProductIds.length === 0) {
      return filteredPlans;
    }

    return filteredPlans
      .filter((plan: IPrice) => gym.allowedOnProductIds.find((productId: string) => productId === plan.product.id));
  }

  private getPlansForSpecialOffers(customerCurrency: string | null, gym: Gym) {
    const plans = this.getAllowedPlansForStartSubscription(customerCurrency, gym);
    return plans.filter((plan) => plan.metadata.specialOfferOption === 'true');
  }

  private filterPrices(
    prices: IPrice[],
    accountId: string,
    customerCurrency: string | null,
    productCategories?: ProductCategory[],
  ) {
    let filteredPlans: IPrice[] = prices.filter((plan: IPrice) => plan.active
      && plan.accountId === accountId);

    if (customerCurrency) {
      filteredPlans = filteredPlans.filter((plan: IPrice) => plan.currency === customerCurrency);
    }

    if (productCategories) {
      filteredPlans = filteredPlans.filter((price: IPrice) => {
        const actualProductCategories: string[] = price.product.metadata?.productCategories?.split(' ,');
        return this.productCategoriesMatchFilter(
          actualProductCategories as ProductCategory[],
          productCategories,
        );
      });
    }

    return filteredPlans;
  }

  private getEarliestStart(
    phase: IStripeSchedulePhase,
    phaseBefore: IStripeSchedulePhase | undefined,
    requestTime: Date,
    contractStartDate: Date | undefined,
  ) {
    let earliestStart = moment(requestTime).toDate();
    const firstPhaseStartDate = new Date(phase.start_date * 1000);
    if (firstPhaseStartDate.getTime() > earliestStart.getTime()) {
      earliestStart = moment(phase.start_date * 1000).toDate();
    }

    const isPhaseBeforePartOfContract = phaseBefore
      && contractStartDate
      && new Date(phaseBefore.start_date * 1000) < contractStartDate;
    if (!phaseBefore || isPhaseBeforePartOfContract) {
      return moment(earliestStart).add(1, 'day').toDate();
    }

    const priceOfPhaseBefore = this.paymentService.getPriceInfo(phaseBefore.plans[0].plan);
    const phaseBeforeIsFreezeOrFreeWeekPrice = priceOfPhaseBefore
      ? this.isFreezeOrFreePrice(priceOfPhaseBefore)
      : false;

    if (!phaseBeforeIsFreezeOrFreeWeekPrice) {
      earliestStart = moment(earliestStart).add(1, 'day').toDate();
    }

    return earliestStart;
  }

  private getLatestEnd(
    phase: IStripeSchedulePhase,
    phaseAfter: IStripeSchedulePhase | undefined,
  ) {
    let latestEnd = moment(phase.end_date * 1000);

    if (!phaseAfter) {
      return latestEnd.subtract(1, 'day').toDate();
    }

    const priceOfPhaseBefore = this.paymentService.getPriceInfo(phaseAfter.plans[0].plan);
    const phaseAfterIsFreezeOrFreeWeekPrice = priceOfPhaseBefore
      ? this.isFreezeOrFreePrice(priceOfPhaseBefore)
      : false;

    if (!phaseAfterIsFreezeOrFreeWeekPrice) {
      latestEnd = latestEnd.subtract(1, 'day');
    }

    return latestEnd.toDate();
  }

  private phaseBuilder(planId: string, start: Date, end: Date): IStripeSchedulePhase {
    return {
      start_date: Math.trunc(start.getTime() / 1000),
      end_date: Math.trunc(end.getTime() / 1000),
      plans: [{
        plan: planId,
        price: planId,
        quantity: 1,
      }],
    };
  }

  private getStandardPlanPhase(schedule: IStripeSchedule): IStripeSchedulePhase | undefined {
    let standardPlanPhase = schedule.phases && schedule.phases[0];
    if (schedule.phases && schedule.phases.length > 1) {
      const phase = schedule.phases[0];
      if (!phase.plans || phase.plans.length === 0) {
        return standardPlanPhase;
      }
      const planId = phase.plans[0].plan;
      const { start_date } = schedule.phases[0];
      const { end_date } = schedule.phases.slice(-1)[0];
      standardPlanPhase = {
        start_date,
        end_date,
        plans: [{
          plan: planId,
          price: planId,
          quantity: 1,
        }],
      };
    }
    return standardPlanPhase;
  }

  private mapPhase(firstPhaseObj: IStripeSchedulePhase)
    : IStripeSchedulePhase {
    return {
      start_date: firstPhaseObj.start_date,
      end_date: firstPhaseObj.end_date,
      plans: firstPhaseObj.plans,
      ...(firstPhaseObj.coupon && { coupon: firstPhaseObj.coupon }),
    };
  }

  private firstPhaseBuilder(firstPhaseObj: IStripeSchedulePhase, pausePhase: IStripeSchedulePhase)
    : IStripeSchedulePhase {
    return {
      start_date: firstPhaseObj.start_date,
      end_date: pausePhase.start_date,
      plans: firstPhaseObj.plans,
      ...(firstPhaseObj.coupon && { coupon: firstPhaseObj.coupon }),
    };
  }

  private afterPausePhaseBuilder(firstPhaseObj: IStripeSchedulePhase, pausePhase: IStripeSchedulePhase)
    : IStripeSchedulePhase {
    const startDate = pausePhase.end_date;
    const bufferSoStartAndEndDateIsNotTheSame = 60;
    let endDate = firstPhaseObj.end_date;
    if (startDate > endDate) {
      endDate = startDate + bufferSoStartAndEndDateIsNotTheSame;
    }
    return {
      start_date: startDate,
      end_date: endDate,
      plans: firstPhaseObj.plans,
      ...(firstPhaseObj.coupon && { coupon: firstPhaseObj.coupon }),
    };
  }

  private async addPhaseToContract(
    subscriptionAndScheduleInfo: ISubscriptionAndSchedule,
    userProfile: UserProfile,
    phaseToInject: IStripeSchedulePhase,
    isFreezePhase?: boolean,
  ):
    Promise<IStripeSchedule> {
    const { schedule } = subscriptionAndScheduleInfo.subscription;
    const { isContract } = subscriptionAndScheduleInfo;

    if (!schedule) {
      throw new HandledError('The subscription has no schedule.');
    }

    if (!schedule.phases) {
      throw new HandledError('The subscription schedule has no phases.');
    }

    const indexOfPhaseToInjectTo = schedule.phases.findIndex((phase, index, arr) => {
      const isLastPhase = index === arr.length - 1;

      if (isLastPhase && !isContract) {
        return phaseToInject.start_date >= phase.start_date;
      }

      return phaseToInject.start_date >= phase.start_date && phaseToInject.end_date <= phase.end_date;
    });

    if (indexOfPhaseToInjectTo === -1) {
      throw new Error('No valid phase found');
    }

    const phaseToInjectTo = schedule.phases[indexOfPhaseToInjectTo];

    let firstPhaseForPhaseToInject;
    if (phaseToInject.start_date !== phaseToInjectTo.start_date) {
      firstPhaseForPhaseToInject = this.firstPhaseBuilder(phaseToInjectTo, phaseToInject);
    }

    const phasesBeforeInjectedPhase = schedule.phases
      .slice(0, indexOfPhaseToInjectTo)
      .map((phase) => this.mapPhase(phase));

    let lastPhaseForPhaseToInject;
    if (phaseToInjectTo.end_date !== phaseToInject.end_date) {
      lastPhaseForPhaseToInject = this.afterPausePhaseBuilder(phaseToInjectTo, phaseToInject);
    }

    const rawLastPhases = schedule.phases.slice(indexOfPhaseToInjectTo + 1, schedule.phases.length);
    const phasesAfterInjectedPhase = rawLastPhases.map((phase) => this.mapPhase(phase));
    const phasesForUpdate = [
      ...phasesBeforeInjectedPhase,
      ...(firstPhaseForPhaseToInject) ? [firstPhaseForPhaseToInject] : [],
      phaseToInject,
      ...(lastPhaseForPhaseToInject) ? [lastPhaseForPhaseToInject] : [],
      ...phasesAfterInjectedPhase,
    ];

    if (isContract && isFreezePhase) {
      const lengthOfFreezeInDays = moment(new Date(phaseToInject.end_date * 1000))
        .diff(moment(new Date(phaseToInject.start_date * 1000)), 'day');

      // We assume the last phase is the standard plan the contract switched to when concluded and we assume the
      // phase before that is the last phase of the contract
      phasesForUpdate[phasesForUpdate.length - 2] = this.extendPhaseWithDays(
        phasesForUpdate[phasesForUpdate.length - 2],
        lengthOfFreezeInDays,
        true,
      );
      phasesForUpdate[phasesForUpdate.length - 1] = this.extendPhaseWithDays(
        phasesForUpdate[phasesForUpdate.length - 1],
        lengthOfFreezeInDays,
      );
    }

    return this.updateSubscriptionSchedule(userProfile, schedule.id, phasesForUpdate);
  }

  private extendPhaseWithDays(phase: IStripeSchedulePhase, amountOfDaysToAdd: number, onlyExtendToEndDate?: boolean):
  IStripeSchedulePhase {
    let startDate = new Date(phase.start_date * 1000);
    let endDate = new Date(phase.end_date * 1000);

    if (!onlyExtendToEndDate) {
      startDate = moment(startDate).add(amountOfDaysToAdd, 'days').toDate();
    }
    endDate = moment(endDate).add(amountOfDaysToAdd, 'days').toDate();

    return {
      ...phase,
      start_date: startDate.getTime() / 1000,
      end_date: endDate.getTime() / 1000,
    };
  }

  private async addPhaseToStandardPlan(
    subscriptionAndScheduleInfo: ISubscriptionAndSchedule,
    userProfile: UserProfile,
    phase: IStripeSchedulePhase,
  ): Promise<IStripeSchedule> {
    let schedule: IStripeSchedule;
    if (subscriptionAndScheduleInfo.hasShedule) {
      schedule = subscriptionAndScheduleInfo.subscription.schedule!;
    } else {
      const createdSchedule = await this.paymentService.createScheduleFromSubscription(
        userProfile,
        subscriptionAndScheduleInfo.subscription.id,
      );

      schedule = {
        id: createdSchedule.id,
        end_behavior: createdSchedule.end_behavior,
        current_phase: createdSchedule.current_phase as IStripeSchedulePhase,
        customer: createdSchedule.customer as string,
        phases: createdSchedule.phases ? createdSchedule.phases.map(({
          start_date, end_date, plans, coupon,
        }) => ({
          start_date,
          ...(coupon && { coupon: coupon as string }),
          end_date,
          plans: plans && plans.map(({ plan }) => {
            const mappedPlan = plan as string;

            return {
              plan: mappedPlan,
              price: mappedPlan,
              quantity: 1,
            };
          }),
        })) : [],
      };
    }

    // normalise standard plan schedule to one phase if not already
    const standardPlanPhase = this.getStandardPlanPhase(schedule);

    if (!standardPlanPhase) {
      throw new HandledError('Could not create a standard plan phase.');
    }

    // add the pause
    const firstPhase = this.firstPhaseBuilder(standardPlanPhase, phase);
    const phaseAfterPause = this.afterPausePhaseBuilder(standardPlanPhase, phase);
    const phasesForUpdate = [firstPhase, phase, phaseAfterPause];

    return this.updateSubscriptionSchedule(userProfile, schedule.id, phasesForUpdate);
  }

  private async updateSubscriptionSchedule(
    userProfile: UserProfile,
    subscriptionScheduleId: string,
    phasesForUpdate: IStripeSchedulePhase[],
  ): Promise<IStripeSchedule> {
    const result = await this.paymentService.updateSubscriptionSchedule(
      subscriptionScheduleId,
      {
        phases: phasesForUpdate,
      },
      userProfile,
    );

    if (result.id !== subscriptionScheduleId) {
      throw new HandledError(
        `Could not update the subscription schedule: ${subscriptionScheduleId}`,
      );
    }

    return {
      id: result.id,
      end_behavior: result.end_behavior,
      phases: phasesForUpdate,
      current_phase: result.current_phase as IStripeSchedulePhase,
      customer: result.customer as string,
    };
  }

  private async getCancelSubscriptionInfo(
    subscription: IStripeCustomerSubscriptionDataWrapper,
    cancelDate: Date,
  ): Promise<ICancelSubscription> {
    const contractDurationDetails = MemberContractDetails.calculateContractDetails(this.logger, subscription);
    if (contractDurationDetails.isContract && !this.hasSubscriptionOnceoffPrice(subscription)) {
      return this.getContractPlanCancelSubscriptionDetails(subscription, cancelDate, contractDurationDetails);
    }

    return this.getStandardPlanCancelSubscriptionDetails(subscription);
  }

  private async getStartSubscriptionInfo(
    subscriptionAndSchedule: ISubscriptionAndSchedule | undefined,
    customer: IStripeCustomerDetailsWrapper,
    gym: Gym,
  ): Promise<IStartSubscription> {
    const enabled = !(subscriptionAndSchedule && subscriptionAndSchedule.subscription
      && ((subscriptionAndSchedule.subscription.status
          && BaseUserProfile.subscriptionStatusIsActive(subscriptionAndSchedule.subscription.status))
        || subscriptionAndSchedule.subscription.cancel_at));

    if (!enabled) {
      return {
        enabled,
        message: 'Cannot start subscription when another subscription is active',
        allowedPlans: [],
      };
    }

    return {
      enabled,
      allowedPlans: this.getAllowedPlansForStartSubscription(customer.currency, gym),
      message: 'Starting a new subscription',
    };
  }

  private getContractPlanCancelSubscriptionDetails(
    subscription: IStripeCustomerSubscriptionDataWrapper,
    cancelDate: Date,
    contractDurationDetails: IContractDurationDetails,
  ): ICancelSubscription {
    if (!subscription.plan) {
      throw new HandledError(
        `Failed to get cancel contract plan subscription details for subscription ${subscription.id}`,
      );
    }

    const { contractEnd, contractStart } = contractDurationDetails;
    if (!contractEnd || !contractStart) {
      return {
        message: 'Missing contract duration details',
        date: cancelDate,
        defaultPenaltyAmount: 0,
        enabled: false,
        enabledCancellationFeeOverride: true,
      };
    }

    const amountLeftOnPlan = subscription.plan.amount || 0;

    let message = 'Cancelling limited contract.';
    const totalDuration = contractEnd - contractStart;
    message += ` Total duration of contract: ${(totalDuration / (1000 * 60 * 60 * 24)).toFixed(2)} days.`;
    const timeLeft = contractEnd - cancelDate.getTime();
    message += ` Time left on contract: ${(timeLeft / (1000 * 60 * 60 * 24)).toFixed(2)} days.`;
    const percentageContractLeft = (timeLeft / totalDuration);
    message += ` Contract left: ${(percentageContractLeft * 100).toFixed(2)}%.`;
    const subscriptionInterval = subscription.plan.interval;
    const totalCyclesInContract = moment(contractEnd).diff(contractStart, subscriptionInterval);
    message += ` Total cycles left in contract: ${totalCyclesInContract} ${subscriptionInterval}.`;
    const discountPercentage = subscription.discount ? subscription.discount.coupon.percent_off || 0 : 0;
    const subscriptionAmount = amountLeftOnPlan / 100;
    const discountedPrice = subscriptionAmount * (discountPercentage / 100);
    const totalContractAmount = (subscriptionAmount - discountedPrice) * totalCyclesInContract;
    message += ` Total contract amount: $${totalContractAmount.toFixed(2)}.`;
    const remainingContractPayments = totalContractAmount * percentageContractLeft;
    message += ` Remaining amount left to pay on contract: $${remainingContractPayments.toFixed(2)}.`;
    const cancellationFee = parseFloat((remainingContractPayments * 0.5).toFixed(2));
    message += ` Final cancellation fee: $${cancellationFee}.`;

    return {
      message,
      date: new Date(subscription.current_period_end * 1000),
      defaultPenaltyAmount: cancellationFee,
      enabled: true,
      enabledCancellationFeeOverride: true,
    };
  }

  private getStandardPlanCancelSubscriptionDetails(
    subscription: IStripeCustomerSubscriptionDataWrapper,
  ): ICancelSubscription {
    return {
      date: new Date(subscription.current_period_end * 1000),
      defaultPenaltyAmount: 0,
      enabled: true,
      message: 'Cancelling standard subscription at end of the billing period',
      enabledCancellationFeeOverride: false,
    };
  }

  private hasSubscriptionOnceoffPrice(subscription: IStripeCustomerSubscriptionDataWrapper): boolean {
    if (!subscription.plan) {
      return false;
    }

    const { onceoff: onceOff } = subscription.metadata;
    return !!(onceOff && subscription.plan.amount === 0);
  }

  private async getCustomer(userProfile: UserProfile) {
    const customerInfo = await this.paymentService.getCustomerAndSubscriptionSchedule(userProfile);
    if (!customerInfo) {
      throw new HandledError(`No customer found: ${userProfile.stripeUserId}`);
    }

    return customerInfo as IStripeCustomerDetailsWrapper;
  }

  private filterCoupons(
    coupons: IStripeCouponWrapper[],
    accountId: string,
    currency: string | undefined,
    productCategories?: ProductCategory[],
  ) {
    const filteredCoupons = coupons.filter((coupon: IStripeCouponWrapper) => {
      if (coupon.accountId !== accountId) {
        return false;
      }

      if (productCategories) {
        const productCategoriesOnCoupon = coupon.metadata.productCategories?.split(', ');
        return coupon.valid
          && this.productCategoriesMatchFilter(productCategoriesOnCoupon as ProductCategory[], productCategories);
      }

      return coupon.valid;
    });

    if (!currency) {
      return filteredCoupons;
    }

    return filteredCoupons.filter((coupon: IStripeCouponWrapper) => {
      if (coupon.amount_off) {
        return (coupon.currency === currency);
      }

      return true;
    });
  }

  private productCategoriesMatchFilter(
    actualProductCategories: ProductCategory[],
    filterProductCategories: ProductCategory[],
  ): boolean {
    return actualProductCategories?.some((pc) => filterProductCategories?.includes(pc as ProductCategory));
  }
}
